import React, { useState, useEffect, useRef } from "react";
import { apiPOST } from "../../utilities/apiHelpers";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { State, City } from 'country-state-city';
import ButtonWithLoader from "../Button/ButtonWithLoader";

export default function Popup({ isOpen, setIsOpen, onUpdate }) {
  // const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({ isoCode: 'IN', name: 'India' });
  const [selectedState, setSelectedState] = useState({ isoCode: '', name: '' });
  const [selectedCity, setSelectedCity] = useState('');
  const [addressFormData, setAddressFormData] = useState({
    Name: '',
    addressLine1: '',
    addressLine2: '',
    zip: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const popupRef = useRef(null);

useEffect(() => {
  if (isOpen) {
    // Clear the form data
    setAddressFormData({ Name: '', addressLine1: '', addressLine2: '', zip: '' });
    // Clear dropdowns
    setSelectedState({ isoCode: '', name: '' });
    setSelectedCity('');
    // Clear any previous red error messages
    setErrors({});
  }
}, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // useEffect(() => {
  //   setCountries(Country.getAllCountries());
  // }, []);

  useEffect(() => {
    if (selectedCountry.isoCode) {
      const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
      if (countryStates.length === 0) {
        setStates([{ isoCode: 'NONE', name: selectedCountry.name }]);
        setSelectedState({ isoCode: 'NONE', name: selectedCountry.name });
      } else {
        setStates(countryStates);
        setSelectedState({ isoCode: '', name: '' });
      }
      setCities([]);
      setSelectedCity('');
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState.isoCode) {
      if (selectedState.isoCode === 'NONE') {
        setCities([{ name: selectedState.name }]);
        setSelectedCity(selectedState.name);
        return;
      }
      const stateCities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
      if (stateCities.length === 0) {
        setCities([{ name: selectedState.name }]);
        setSelectedCity(selectedState.name);
      } else {
        setCities(stateCities);
        setSelectedCity('');
      }
    }
  }, [selectedState, selectedCountry]);

  // const handleCountryChange = (e) => {
  //   const selectedOption = countries.find(country => country.isoCode === e.target.value);
  //   setSelectedCountry({ isoCode: selectedOption.isoCode, name: selectedOption.name });
  // };

  const handleStateChange = (e) => {
    const selectedOption = states.find(state => state.isoCode === e.target.value);
    setSelectedState({ isoCode: selectedOption.isoCode, name: selectedOption.name });
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  const togglePopup = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddressFormData({
      ...addressFormData,
      [name]: value
    });
  };

  const validateForm = () => {
    const newErrors = {};
    const alphaRegex = /^[A-Za-z\s]+$/;
    const zipRegex = /^\d{6}$/;

    if (!addressFormData.Name.trim()) {
      newErrors.Name = "Name is required";
    } else if (!alphaRegex.test(addressFormData.Name)) {
      newErrors.Name = "Enter alphabets only";
    }

    if (!addressFormData.addressLine1.trim()) {
      newErrors.addressLine1 = "Address 1 is required";
    }

    if (!addressFormData.zip.trim()) {
      newErrors.zip = "Pincode is required";
    } else if (!zipRegex.test(addressFormData.zip)) {
      newErrors.zip = "Pincode should be exactly 6 digits";
    }

    if (!selectedCountry.name) {
      newErrors.country = "Country is required";
    }

    if (!selectedState.name) {
      newErrors.state = "State is required";
    }

    if (!selectedCity) {
      newErrors.city = "City is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    const addAddressPayload = {
      ...addressFormData,
      country: selectedCountry.name,
      city: selectedCity,
      state: selectedState.name
    };
    try {
      setLoading(true);
      const response = await apiPOST('/v1/address/add-address', addAddressPayload);
      if (response.data.status) {
        setAddressFormData({ Name: '', addressLine1: '', addressLine2: '', zip: '' });
        setSelectedCountry({ isoCode: 'IN', name: 'India' });
        setSelectedState({ isoCode: '', name: '' });
        setSelectedCity('');
        setErrors({});
        togglePopup();
        if (onUpdate) {
          onUpdate();
        }
        toast.success('Address added successfully');
        setLoading(false);
      } else {
        console.error('Error adding address:', response.data.message);
        toast.error(response.data.message || response.data.data || 'Failed to add address');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error('Failed to add address');
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div ref={popupRef} className="bg-white p-4 rounded-lg shadow-lg w-full max-w-[732px] max-h-[90vh] overflow-y-auto scrollbar-custom scroll-smooth relative">
            <button onClick={togglePopup} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
              &times;
            </button>
            <form onSubmit={handleSubmit} className="space-y-4">
              <h1 className="text-xl font-bold mb-4">Add New Address</h1>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block text-gray-700">Name</label>
                  <input
                    type="text"
                    name="Name"
                    value={addressFormData.Name}
                    onChange={handleChange}
                    placeholder="Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                  {errors.Name && <span className="text-xs text-red-500">{errors.Name}</span>}
                </div>
              </div>
              <div>
                <label className="block text-gray-700">Address 1</label>
                <textarea
                  name="addressLine1"
                  value={addressFormData.addressLine1}
                  onChange={handleChange}
                  placeholder="Address 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                {errors.addressLine1 && <span className="text-xs text-red-500">{errors.addressLine1}</span>}
              </div>
              <div>
                <label className="block text-gray-700">Address 2</label>
                <textarea
                  name="addressLine2"
                  value={addressFormData.addressLine2}
                  onChange={handleChange}
                  placeholder="Address 2 (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                {errors.addressLine2 && <span className="text-xs text-red-500">{errors.addressLine2}</span>}
              </div>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block text-gray-700">Pincode</label>
                  <input
                    type="text"
                    name="zip"
                    value={addressFormData.zip}
                    onChange={handleChange}
                    placeholder="Pincode"
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                  {errors.zip && <span className="text-xs text-red-500">{errors.zip}</span>}
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700">Country</label>
                  <select
                    value="IN"
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded cursor-not-allowed appearance-none bg-white"
                  >
                    <option value="IN">India</option>
                  </select>
                  {errors.country && <span className="text-xs text-red-500">{errors.country}</span>}
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block text-gray-700">State</label>
                  <select value={selectedState.isoCode} onChange={handleStateChange} disabled={!selectedCountry.isoCode} className="w-full px-3 py-2 border border-gray-300 rounded">
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state.isoCode} value={state.isoCode}>{state.name}</option>
                    ))}
                  </select>
                  {errors.state && <span className="text-xs text-red-500">{errors.state}</span>}
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700">City</label>
                  <select value={selectedCity} onChange={handleCityChange} disabled={!selectedState.isoCode} className="w-full px-3 py-2 border border-gray-300 rounded">
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.name} value={city.name}>{city.name}</option>
                    ))}
                  </select>
                  {errors.city && <span className="text-xs text-red-500">{errors.city}</span>}
                </div>
              </div>
              <ButtonWithLoader type={'submit'} loading={loading} buttonText={"Save & Update"} width={"w-[200px]"} />
            </form>
          </div>
        </div>
      )}
    </div>
  );
}